import React, { Suspense, lazy } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthenticatedUserProvider } from './contexts/AuthContext';
import { lightTheme, darkTheme } from './theme';
import Layout from './components/Layout';
import './styles/global.css';
import Contact from './pages/Contact';
import AboutPage from './pages/About';
import LoginPage from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ActivationCode from './pages/Auth/ActivationCode';
import SocialCompletePage from './pages/Auth/SocialComplete';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/i18n';
import Portal from './pages/Portal';
import AdvertisementDetail from './pages/AdvertisementDetail';
import BlogDetail from './pages/BlogDetail';
import Blog from './pages/Blog';
import PrivateRoute from './components/PrivateRoute';
import Profile from './pages/Profile';
import EditProfile from './pages/Profile/EditProfile';
import ChangePassword from './pages/Profile/ChangePassword';
import { CustomThemeProvider, useThemeMode } from './contexts/ThemeContext';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import FAQ from './pages/FAQ';
import HelpCenter from './pages/HelpCenter';
import Safety from './pages/Safety';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Cookies from './pages/Cookies';
import {
  KvkkPage,
  CommercialConsentPage,
  ExplicitConsentPage,
  CrmAgreementPage,
  CrmNoticePage,
} from './pages/Legal';
import CookieBanner from './components/CookieBanner';
import PageTracker from './components/PageTracker';
import ComingSoonDialog from './components/ComingSoon';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ActivationRoute from './components/ActivationRoute';
import ForgotPassword from './pages/Auth/ForgotPassword';
import EditEmail from './pages/Profile/EditEmail';
import EditPhone from './pages/Profile/EditPhone';
import EditMate from './pages/Profile/EditMate';
import SupportForm from './pages/Support/SupportForm';
import ProtectedRoute from './components/ProtectedRoute';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import NotificationsPage from './pages/Notifications';
import ChatRoomList from './pages/Chat';
import ChatRequestList from './pages/Chat/ChatRequests';
import ChatScreen from './pages/Chat/ChatScreen';
import RoommateDetail from './pages/RoommateDetail';
import MyAdvertisements from './pages/MyAdvertisements';
import EditRoommate from './pages/EditRoommate';
import AddRoommate from './pages/AddRoommate';
import IdentityVerification from './pages/Profile/IdentityVerification';
import NotificationSettings from './pages/NotificationSettings';
import AppRedirect from './pages/AppRedirect';
import { useLocation } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
// CRM ayrı parça: site ziyaretçileri CRM kodunu indirmez, yalnızca /crm açılınca yüklenir
const CrmRoutes = lazy(() => import('./crm/CrmRoutes'));
// Tanıtım sayfaları ayrı parçalar: yalnızca açıldıklarında yüklenir
const OdazenLanding = lazy(() => import('./pages/Landing/OdazenLanding'));
const CrmLanding = lazy(() => import('./pages/Landing/CrmLanding'));
// Ana sayfa portal (/); ilan listesi (eski ana sayfa, sekmeli) /ilanlar'da; herkese açık hesaplama araçları /araclar
const ListingsPage = lazy(() => import('./pages/Home'));
const PublicTools = lazy(() => import('./pages/PublicTools'));
// Mülk Takibi (ev sahipleri): /mulk-takibi tanıtım, /mulk-takibi/panel/* uygulama
const PropertyTracking = lazy(() => import('./pages/PropertyTracking'));
// Kiracı portalı: /kiraci/davet/:code (girişsiz davet önizlemesi), /kiraci ve /kiraci/:tenancyId
const TenantPortal = lazy(() => import('./pages/TenantPortal'));
// Kayıtlı aramalarım (yeni ilan özeti bildirimleri, tür 24): /kayitli-aramalar
const SavedSearches = lazy(() => import('./pages/SavedSearches'));
const landingFallback = <div style={{ minHeight: '70vh' }} />;
// Tam ekran yer tutucu: parça yüklenirken alt bilgi görünür alana girip sonra kaymasın (CLS)
const pageFallback = <div style={{ minHeight: '100vh' }} />;

function ConsumerApp() {
  return (
    <>
      <Routes>
        <Route path="/short" element={<AppRedirect />} />
      </Routes>
      <Layout>
        <Routes>
          <Route path="/" element={<Portal />} />
          <Route path="/ilanlar" element={<Suspense fallback={pageFallback}><ListingsPage /></Suspense>} />
          <Route path="/araclar/*" element={<Suspense fallback={pageFallback}><PublicTools /></Suspense>} />
          <Route path="/mulk-takibi/*" element={<Suspense fallback={landingFallback}><PropertyTracking /></Suspense>} />
          <Route path="/kiraci/*" element={<Suspense fallback={landingFallback}><TenantPortal /></Suspense>} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/tanitim" element={<Suspense fallback={landingFallback}><OdazenLanding /></Suspense>} />
          <Route path="/emlak-crm" element={<Suspense fallback={landingFallback}><CrmLanding /></Suspense>} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<Register />} />
          <Route path="/social-complete" element={<SocialCompletePage />} />
          <Route path="/activation" element={<ActivationCode />} />
          <Route path="/advertisements" element={<Navigate to="/ilanlar?tab=homes" replace />} />
          <Route path="/advertisements/:id" element={<AdvertisementDetail />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/profile/edit-email" element={<ProtectedRoute><EditEmail /></ProtectedRoute>} />
          <Route path="/profile/edit-phone" element={<ProtectedRoute><EditPhone /></ProtectedRoute>} />
          <Route path="/profile/edit-mate" element={<ProtectedRoute><EditMate /></ProtectedRoute>} />
          <Route path="/profile/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/safety" element={<Safety />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/kvkk" element={<KvkkPage />} />
          <Route path="/ticari-ileti-onayi" element={<CommercialConsentPage />} />
          <Route path="/acik-riza" element={<ExplicitConsentPage />} />
          <Route path="/crm-sozlesmesi" element={<CrmAgreementPage />} />
          <Route path="/crm-aydinlatma" element={<CrmNoticePage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/support" element={<ProtectedRoute><SupportForm /></ProtectedRoute>} />
          <Route path="/roommate-search" element={<Navigate to="/ilanlar?tab=roommates" replace />} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="/notification-settings" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
          <Route path="/chat" element={<ProtectedRoute><ChatRoomList /></ProtectedRoute>} />
          <Route path="/chat/requests" element={<ProtectedRoute><ChatRequestList /></ProtectedRoute>} />
          <Route path="/chat/:id" element={<ProtectedRoute><ChatScreen /></ProtectedRoute>} />
          <Route path="/advertisement/:id" element={<AdvertisementDetail />} />
          <Route path="/roommate-detail/:id" element={<RoommateDetail />} />
          <Route path="/my-advertisements" element={<ProtectedRoute><MyAdvertisements /></ProtectedRoute>} />
          <Route path="/kayitli-aramalar" element={<ProtectedRoute><Suspense fallback={pageFallback}><SavedSearches /></Suspense></ProtectedRoute>} />
          <Route path="/edit-roommate/:id" element={<ProtectedRoute><EditRoommate /></ProtectedRoute>} />
          <Route path="/add-roommate" element={<ProtectedRoute><AddRoommate /></ProtectedRoute>} />
          <Route path="/profile/*" element={<PrivateRoute><Profile /></PrivateRoute>} />
          <Route path="/profile/identity-verification" element={<PrivateRoute><IdentityVerification /></PrivateRoute>} />
        </Routes>
      </Layout>
    </>
  );
}

function AppShell() {
  const location = useLocation();
  // Yalnızca /crm ve /crm/... CRM uygulamasıdır; /crm-sozlesmesi, /crm-aydinlatma gibi yasal sayfalar tüketici sitesinde
  if (/^\/crm(\/|$)/.test(location.pathname)) {
    return (
      <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
        <CrmRoutes />
      </Suspense>
    );
  }
  return <ConsumerApp />;
}

function ThemedApp() {
  const { i18n } = useTranslation();
  const { theme } = useThemeMode();
  const [showComingSoon, setShowComingSoon] = React.useState(false);
  const dateLocale = i18n.language === 'tr' ? tr : enUS;

  return (
    <I18nextProvider i18n={i18n}>
      {/* Her zaman bağlı: SEO bileşeni olmayan sayfaya geçişte <html lang> boşalmasın */}
      <Helmet htmlAttributes={{ lang: i18n.language }} />
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthenticatedUserProvider>
            <BrowserRouter>
              <ScrollToTop />
              <AppShell />
              <PageTracker />
              <CookieBanner />
              <ComingSoonDialog
                open={showComingSoon}
                onClose={() => setShowComingSoon(false)}
              />
            </BrowserRouter>
          </AuthenticatedUserProvider>
          <ToastContainer />
        </ThemeProvider>
      </LocalizationProvider>
    </I18nextProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <CustomThemeProvider>
        <ThemedApp />
      </CustomThemeProvider>
    </HelmetProvider>
  );
}

export default App;
